// Offline art viewer (?gallery). Uses exactly the same cached meshes as the game.

import { Box3, Group, Material, Mesh, Scene, Vector3 } from 'three'
import { BUILDINGS as DEFS, TOWNHALL_FOOTPRINT } from '../../shared/constants'
import { hexToPixel } from '../../shared/hex'
import { TOWN_HALL_PHASES, townHallPhase } from '../../shared/townhall'
import { HEX_SIZE } from '../state'
import type { OrbitCam } from './camera'
import type { BuildingType } from '../../shared/types'
import { buildingGeometry } from './models3d/buildings3d'
import { unitGeometry } from './models3d/units3d'
import { MeshBuilder } from './models3d/mesh'

const BUILDINGS: BuildingType[] = [
  'townhall', 'house', 'farm', 'chop', 'quarry', 'mine', 'storehouse', 'barracks',
  'workbench', 'dummy', 'palisade', 'pier', 'tower', 'ramp', 'camp',
]
const LOOKS = ['idle', 'carrier', 'archer', 'spearman', 'swordsman', 'hammer', 'rod', 'hoe', 'axe', 'pick']
const ANIMALS = ['deer', 'wolf', 'bear']

/** Selectable models on a hexagonal podium, with an orbit control. */
export function buildGallery(scene: Scene, mat: Material, orbit: OrbitCam): { x: number; z: number } {
  const group = new Group()
  scene.add(group)
  const base = new MeshBuilder()
  base.cyl(0, -5, 0, 72, 5, 6, '#8f9e74')
  base.cyl(0, -9, 0, 73, 4, 6, '#677665')
  const podium = new Mesh(base.geometry(), mat)
  podium.receiveShadow = true
  scene.add(podium)
  // Identical reserved ground at every hall stage, with the actual seven hexes.
  const hallGround = new MeshBuilder()
  hallGround.cyl(0, -7, 0, HEX_SIZE, 4, 6, '#63745a')
  hallGround.cyl(0, -3, 0, HEX_SIZE - 0.35, 3, 6, '#a9b286')
  const hexGeometry = hallGround.geometry().rotateY(Math.PI / 6)
  const plot = new Group()
  for (const [q, r] of TOWNHALL_FOOTPRINT) {
    const p = hexToPixel(q, r, HEX_SIZE)
    const hex = new Mesh(hexGeometry, mat)
    hex.position.set(p.x, 0, p.y)
    hex.receiveShadow = true
    plot.add(hex)
  }
  scene.add(plot)
  const ui = document.getElementById('ui')!
  ui.classList.add('gallery-ui')
  const header = document.createElement('header')
  header.className = 'gallery-heading'
  header.innerHTML = '<span class="gallery-eyebrow">SERF LAND · ART COLLECTION</span><h1>From campfire<br>to stone keep.</h1><p>One plot of land. Five chapters of your village.</p>'
  ui.appendChild(header)
  const panel = document.createElement('section')
  panel.className = 'panel gallery-controls'
  panel.setAttribute('aria-label', 'Model viewer controls')
  panel.innerHTML = '<label for="gallery-model">THE COLLECTION</label><select id="gallery-model"></select><div class="gallery-phases" role="group" aria-label="Town hall phases"></div><h2 class="gallery-name" aria-live="polite"></h2><p class="gallery-description"></p><p class="gallery-meta"></p><label for="gallery-angle">ROTATE MODEL</label><input id="gallery-angle" type="range" min="0" max="360" value="145"><a class="btn" href="/">Return to the game ↗</a>'
  ui.appendChild(panel)
  const select = panel.querySelector('select')!
  const items = [
    ...BUILDINGS.map(type => ({ key: type, name: DEFS[type].name, kind: 'building' })),
    ...LOOKS.map(look => ({ key: look, name: look === 'idle' ? 'Villager' : `Villager · ${look}`, kind: 'person' })),
    ...ANIMALS.map(key => ({ key, name: key[0].toUpperCase() + key.slice(1), kind: 'animal' })),
  ]
  items.forEach((item, i) => {
    const option = document.createElement('option')
    option.value = String(i); option.textContent = item.name
    select.appendChild(option)
  })
  let phase = townHallPhase(Number(new URLSearchParams(location.search).get('phase') ?? 1))
  const phaseControls = panel.querySelector<HTMLDivElement>('.gallery-phases')!
  const phaseButtons = TOWN_HALL_PHASES.map(stage => {
    const button = document.createElement('button')
    button.type = 'button'
    button.textContent = String(stage.phase)
    button.setAttribute('aria-label', `Phase ${stage.phase}: ${stage.name}`)
    button.title = stage.name
    button.onclick = () => { phase = stage.phase; show(true) }
    phaseControls.appendChild(button)
    return button
  })
  const show = (preserveAngle = false): void => {
    const item = items[Number(select.value)]
    const isHall = item.kind === 'building' && item.key === 'townhall'
    const stage = TOWN_HALL_PHASES[phase - 1]
    phaseControls.hidden = !isHall
    podium.visible = !isHall
    plot.visible = isHall
    phaseButtons.forEach((button, i) => button.setAttribute('aria-pressed', String(i + 1 === phase)))
    group.clear() // shared, cached geometry stays alive
    const geometry = item.kind === 'building'
      ? buildingGeometry(item.key as BuildingType, '#6e9990', 0, isHall && phase > 1 ? Math.min(3, phase - 1) | (((1 << Math.min(phase, 6)) - 1) << 2) : 0, phase)
      : unitGeometry(item.kind === 'person' ? 'citizen' : item.key, item.key, '#6e9990')
    const mesh = new Mesh(geometry, mat)
    mesh.castShadow = mesh.receiveShadow = true
    const bounds = new Box3().setFromObject(mesh)
    const center = bounds.getCenter(new Vector3())
    if (!isHall) { mesh.position.x -= center.x; mesh.position.z -= center.z }
    group.add(mesh)
    const size = bounds.getSize(new Vector3())
    podium.scale.setScalar(Math.max(size.x, size.z, size.y) / 95)
    orbit.dist = isHall ? 255 : Math.max(65, size.length() * 2.3)
    orbit.pitch = isHall ? 0.7 : 0.55
    if (!preserveAngle) {
      const angle = isHall ? 325 : 145
      orbit.yaw = orbit.yawTarget = angle * Math.PI / 180
      panel.querySelector<HTMLInputElement>('input')!.value = String(angle)
    }
    panel.querySelector('.gallery-name')!.textContent = isHall ? `${phase}. ${stage.name}` : item.name
    panel.querySelector('.gallery-description')!.textContent = isHall ? stage.description : ''
    panel.querySelector('.gallery-meta')!.textContent = isHall
      ? 'Town Hall · 7 hexes at every phase · same camera scale'
      : `${(geometry.attributes.position.count / 3).toLocaleString()} triangles · flat shaded · in-game mesh`
  }
  select.onchange = () => show()
  panel.querySelector<HTMLInputElement>('input')!.oninput = e => {
    orbit.yawTarget = Number((e.target as HTMLInputElement).value) * Math.PI / 180
  }
  show()
  return { x: 0, z: 0 }
}
